import { AuthStatus, LoginRequest, LogoutResponse } from "../models/auth";
import { NotificationChannel, NotificationRequest, NotificationRequestV2, NotifyResult, PaginatedEditableNotificationsResponse } from "../models/notification";
import { TelegramProxyLink, TelegramProxyLinkRequest } from "../models/proxy";
import { OnlineUsersStatsPoint, ServerStatsResponse } from "../models/stats";
import { PaginatedSyncLinksResponse, SyncFilter, SyncLinksPageParams, SyncStatus } from "../models/sync";
import { AdminUserInfoDetailsDto, AdminUserInfoDto, PaginatedUsersResponse, UsersPageParams } from "../models/user";
import { XuiClient, XuiClientsResponse, XuiHostRequest, XuiServerConfig } from "../models/xui";
import { CacheStatsResponse, CookieOptions, IApiClient, ResultResponse } from "./IApiClient";

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const mockUsers: AdminUserInfoDto[] = Array.from({ length: 50 }, (_, i) => ({
	id: `user-${i + 1}`,
	tgName: `User${i + 1}`,
	tgId: `${1000000000 + i}`,
}));

const mockUserDetails: Record<string, AdminUserInfoDetailsDto> = {
	"user-1": {
		userId: "user-1",
		tgName: "User1",
		tgId: "1000000000",
		subInfo: {
			subId: "sub-1",
			beginDate: "2025-01-01T00:00:00Z",
			endDate: "2026-01-01T00:00:00Z",
			type: "PREMIUM",
		},
		payments: [
			{
				id: "pay-1",
				youKassaPaymentId: "pay-123456",
				type: "SUBSCRIPTION",
				status: "SUCCEEDED",
				creationTs: "2025-01-01T00:00:00Z",
				updateTs: "2025-01-01T00:00:00Z",
			},
		],
	},
};

const mockXuiServers: XuiServerConfig[] = [
	{
		identity: "server-1",
		name: "US Server 1",
		frendlyname: "US Server 1 (New York)",
		inboundId: 1,
		host: "us1.example.com",
		user: "admin",
		password: "password123",
		active: true,
	},
	{
		identity: "server-2",
		name: "EU Server 1",
		frendlyname: "EU Server 1 (Frankfurt)",
		inboundId: 2,
		host: "eu1.example.com",
		user: "admin",
		password: "password123",
		active: false,
	},
];

const mockSyncLinks = Array.from({ length: 20 }, (_, i) => ({
	userId: `user-${i + 1}`,
	tgUserName: `User${i + 1}`,
	serverId: `server-${(i % 3) + 1}`,
	status: i % 5 === 0 ? SyncStatus.FAILED : i % 3 === 0 ? SyncStatus.PENDING : SyncStatus.SYNCED,
	lastAttempt: i % 5 === 0 ? "2025-03-20T10:00:00Z" : undefined,
	retries: i % 5 === 0 ? 3 : undefined,
	lastError: i % 5 === 0 ? "Connection timeout" : undefined,
}));

const mockTelegramProxyLinks: TelegramProxyLink[] = [
	{
		id: 1,
		friendlyName: "Основной MTProxy",
		url: "https://t.me/proxy?server=185.94.167.114&port=443&secret=00000000000000000000000000000000",
	},
	{
		id: 2,
		friendlyName: "Резервный MTProxy",
		url: "tg://proxy?server=185.94.167.115&port=443&secret=11111111111111111111111111111111",
	},
];

export class MockApiClient implements IApiClient {
	async getHostLatency(identity: string): Promise<{ result: number; success: boolean; error?: string }> {
		return { result: 100, success: true }
	}

	onUnauthorized?: (() => void) | undefined;

	private authToken: string | null = "mock-token";

	async login(credentials: LoginRequest): Promise<AuthStatus> {
		await delay(500);
		if (credentials.username === "admin" && credentials.password === "admin") {
			this.authToken = "mock-token";
			return { success: true, result: { username: credentials.username } };
		}
		throw new Error("Invalid credentials");
	}

	async logout(): Promise<LogoutResponse> {
		await delay(300);
		this.authToken = null;
		return { success: true, message: "Logged out successfully" };
	}

	async checkAuthStatus(): Promise<AuthStatus> {
		await delay(200);
		if (this.authToken) {
			return { success: true, result: { username: "admin" } };
		}
		return { success: false, result: { username: "" } };
	}

	isAuthenticated(): boolean {
		return !!this.authToken;
	}

	getAuthToken(): string | null {
		return this.authToken;
	}

	setAuthToken(token: string, options?: CookieOptions): void {
		this.authToken = token;
	}

	clearAuthToken(): void {
		this.authToken = null;
	}

	async sendNotification(request: NotificationRequest, files?: File[]): Promise<NotifyResult> {
		await delay(800);
		return {
			total: 42,
			success: true,
			message: "Notifications sent successfully",
		};
	}

	async sendNotificationV2(request: NotificationRequestV2, files?: File[]): Promise<NotifyResult> {
		await delay(800);
		if (request.channel === "EMAIL") {
			return {
				total: 0,
				success: false,
				message: "Email notifications are not implemented yet",
			};
		}
		return {
			total: 42,
			success: true,
			message: "Telegram notifications queued successfully",
		};
	}

	async listNotifications(params: { page?: number; size?: number } = {}): Promise<PaginatedEditableNotificationsResponse> {
		await delay(250);
		const { page = 0, size = 20 } = params;
		return {
			content: [
				{
					id: "notif-1001",
					channel: "TELEGRAM",
					notification: {
						parseMode: "NONE",
						disableWebPagePreview: false,
						blocks: [{ type: "TEXT", text: "Привет! Это история отправленного уведомления." }],
					},
					ts: "2025-01-01T12:00:00Z",
				},
			],
			totalElements: 1,
			totalPages: 1,
			size,
			number: page,
		};
	}

	async editNotification(notificationId: string, payload: any, channels: NotificationChannel[] = []): Promise<ResultResponse<void>> {
		await delay(250);
		return { success: true, result: undefined, message: `Notification ${notificationId} updated` };
	}

	async getUsersPaginated(params: UsersPageParams = {}): Promise<PaginatedUsersResponse> {
		await delay(400);
		const { page = 0, pageSize = 20 } = params;
		const start = page * pageSize;
		const data = mockUsers.slice(start, start + pageSize);
		return {
			data,
			total: mockUsers.length,
			page,
			pageSize,
			hasMore: start + pageSize < mockUsers.length,
		};
	}

	async getUserDetails(userId: string): Promise<AdminUserInfoDetailsDto> {
		await delay(300);
		return (
			mockUserDetails[userId] || {
				userId,
				tgName: `User ${userId}`,
				tgId: "1000000000",
				subInfo: undefined,
				payments: [],
			}
		);
	}

	async updateUserSub(
		userId: string,
		params: { type?: string; end?: string; durationDays?: number },
		request?: any
	): Promise<any> {
		await delay(500);
		return { success: true, userId, ...params };
	}

	async getXuiClients(params?: { flow?: string }): Promise<XuiClientsResponse> {
		await delay(400);
		return {
			clients: [
				{ id: "1", email: "user1@test.com", enable: true, expiryTime: 0, totalGB: 0, reset: 0, flow: "xtls-rprx-vision", limitIp: 0, subId: "", tgId: "", created_at: 0, updated_at: 0 },
				{ id: "2", email: "user2@test.com", enable: true, expiryTime: 0, totalGB: 0, reset: 0, flow: "xtls-rprx-vision", limitIp: 0, subId: "", tgId: "", created_at: 0, updated_at: 0 },
			],
		};
	}

	async getXuiServers(): Promise<XuiServerConfig[]> {
		await delay(300);
		return mockXuiServers;
	}

	// === XUI Hosts Management (new API) ===
	async getXuiHosts(): Promise<XuiServerConfig[]> {
		await delay(300);
		return mockXuiServers;
	}

	async getXuiHost(identity: string): Promise<XuiServerConfig> {
		await delay(200);
		const server = mockXuiServers.find(s => s.identity === identity);
		if (!server) {
			throw new Error(`Host not found: ${identity}`);
		}
		return server;
	}

	async getSupportedHostTypes(): Promise<string[]> {
		await delay(200);
		return ["3xui"];
	}

	async registerXuiHost(host: XuiHostRequest): Promise<void> {
		await delay(500);
		const newServer: XuiServerConfig = {
			identity: host.identity || `server-${Date.now()}`,
			name: host.frendlyname,
			frendlyname: host.frendlyname,
			inboundId: host.inboundId,
			host: host.host,
			user: host.user,
			password: host.password,
			token: host.token,
			type: host.type,
			active: false,
		};
		mockXuiServers.push(newServer);
	}

	async activateXuiHost(identity: string): Promise<void> {
		await delay(300);
		const server = mockXuiServers.find(s => s.identity === identity);
		if (server) {
			server.active = true;
		}
	}

	async deactivateXuiHost(identity: string): Promise<void> {
		await delay(300);
		const server = mockXuiServers.find(s => s.identity === identity);
		if (server) {
			server.active = false;
		}
	}

	async updateXuiHost(identity: string, host: XuiHostRequest): Promise<void> {
		await delay(400);
		const server = mockXuiServers.find(s => s.identity === identity);
		if (server) {
			server.frendlyname = host.frendlyname;
			server.inboundId = host.inboundId;
			server.host = host.host;
			server.user = host.user;
			server.password = host.password;
			server.token = host.token;
			server.type = host.type;
		}
	}

	async deleteXuiHost(identity: string): Promise<void> {
		await delay(400);
		const index = mockXuiServers.findIndex(s => s.identity === identity);
		if (index !== -1) {
			mockXuiServers.splice(index, 1);
		}
	}

	async addXuiClients(clients: XuiClient[], xuiServerIdentity?: string): Promise<ResultResponse<void>> {
		await delay(600);
		return { success: true, result: undefined };
	}

	async updateXuiGeofile(xuiServerIdentity?: string): Promise<ResultResponse<void>> {
		await delay(500);
		return {success: true, result: undefined };
	}

	async listSyncLinks(params: SyncLinksPageParams = {}): Promise<PaginatedSyncLinksResponse> {
		await delay(400);
		const { page = 0, pageSize = 20 } = params;
		const start = page * pageSize;
		const data = mockSyncLinks.slice(start, start + pageSize);
		return {
			data,
			total: mockSyncLinks.length,
			page,
			pageSize,
			hasMore: start + pageSize < mockSyncLinks.length,
		};
	}

	async syncErrors(filter: SyncFilter): Promise<void> {
		await delay(1000);
	}

	async getServerStats(params?: { step?: string; from?: string; agg: "MAX" | "AVG" }): Promise<ServerStatsResponse> {
		await delay(500);
		const now = new Date();
		const points: ServerStatsResponse = {};

		for (let i = 0; i < 5; i++) {
			const hostId = `server-${i + 1}`;
			points[hostId] = Array.from({ length: 24 }, (_, j) => {
				const ts = new Date(now.getTime() - (23 - j) * 3600000);
				return {
					hostId,
					createdAt: ts.toISOString(),
					systemInfo: {
						identity: hostId,
						netIO: { up: Math.random() * 1000, down: Math.random() * 5000 },
						netTraffic: { sent: Math.random() * 1000000, recv: Math.random() * 5000000 },
					},
				};
			});
		}

		return points;
	}

	async getOnlineUserStats(params?: { from?: string }): Promise<OnlineUsersStatsPoint[]> {
		await delay(400);
		const now = new Date();
		return Array.from({ length: 7 }, (_, i) => {
			const ts = new Date(now.getTime() - (6 - i) * 86400000);
			return {
				ts: ts.toISOString(),
				count: Math.floor(Math.random() * 100) + 50,
				avg: Math.random() * 80 + 40,
				max: Math.floor(Math.random() * 120) + 80,
			};
		});
	}

	async getTelegramProxyLinks(): Promise<TelegramProxyLink[]> {
		await delay(200);
		return [...mockTelegramProxyLinks];
	}

	async createTelegramProxyLink(request: TelegramProxyLinkRequest): Promise<TelegramProxyLink> {
		await delay(300);
		const link: TelegramProxyLink = {
			id: Date.now(),
			friendlyName: request.friendlyName,
			url: request.url,
		};
		mockTelegramProxyLinks.push(link);
		return link;
	}

	async updateTelegramProxyLink(id: number, request: TelegramProxyLinkRequest): Promise<TelegramProxyLink> {
		await delay(300);
		const link = mockTelegramProxyLinks.find((item) => item.id === id);
		if (!link) {
			throw new Error(`Proxy link not found: ${id}`);
		}
		link.friendlyName = request.friendlyName;
		link.url = request.url;
		return link;
	}

	async listCaches(): Promise<string[]> {
		await delay(200);
		return ['servers-latency', 'user-latency', 'user-conf', 'telegram-proxy-links'];
	}

	async getCacheStats(): Promise<CacheStatsResponse> {
		await delay(300);
		return {
			caches: [
				{ name: 'servers-latency',     entryCount: 1,  ttlSeconds: 18000 },
				{ name: 'telegram-proxy-links', entryCount: 4,  ttlSeconds: 7200  },
				{ name: 'user-conf',            entryCount: 17, ttlSeconds: 7200  },
				{ name: 'user-latency',         entryCount: 17, ttlSeconds: 18000 },
			],
			redis: {
				usedMemory: '2.50M',
				usedMemoryPeak: '3.10M',
				usedMemoryRss: '4.00M',
				connectedClients: 3,
				redisVersion: '7.0.0',
			},
		};
	}

	async flushAllCaches(): Promise<ResultResponse<void>> {
		await delay(300);
		return { success: true, result: undefined };
	}

	async flushCache(name: string): Promise<ResultResponse<void>> {
		await delay(200);
		return { success: true, result: undefined };
	}

	async deleteTelegramProxyLink(id: number): Promise<ResultResponse<void>> {
		await delay(300);
		const index = mockTelegramProxyLinks.findIndex((item) => item.id === id);
		if (index !== -1) {
			mockTelegramProxyLinks.splice(index, 1);
		}
		return { success: true, result: undefined };
	}

	async get<T>(path: string, params?: Record<string, string>): Promise<T> {
		await delay(300);
		return {} as T;
	}

	async post<T>(path: string, data?: any): Promise<T> {
		await delay(300);
		return {} as T;
	}

	async put<T>(path: string, data?: any): Promise<T> {
		await delay(300);
		return {} as T;
	}

	async patch<T>(path: string, data?: any): Promise<T> {
		await delay(300);
		return {} as T;
	}

	async delete<T>(path: string): Promise<T> {
		await delay(300);
		return {} as T;
	}

	async uploadFile<T>(path: string, file: File, additionalData?: Record<string, any>): Promise<T> {
		await delay(500);
		return { filename: file.name, size: file.size } as T;
	}

	async postForm<T>(path: string, formData: FormData): Promise<T> {
		await delay(300);
		return {} as T;
	}

	buildUrl(path: string): string {
		return path;
	}
}
